import React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { connect } from "react-redux";
import { setLogout } from "reduxStore/state";
import ErrorImage from "assets/images/error-map.png";

// Class-based ErrorBoundary
class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showDetails: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  handleGoHome = () => {
    window.location.href = "/home";
  };

  handleLogoutAndReset = () => {
    this.props.setLogout();
    window.location.href = "/login"; // Redirect to login after logout
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f7f7f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            textAlign: { xs: "center", md: "left" },
            backgroundColor: "white",
            borderRadius: "1rem",
            boxShadow: 3,
            p: "2rem",
            maxWidth: "900px",
          }}
        >
          <Box
            component="img"
            src={ErrorImage}
            alt="Lost with a map"
            sx={{
              maxWidth: "300px",
              width: "100%",
              mr: { md: "2rem" },
              mb: { xs: "2rem", md: 0 },
            }}
          />

          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Oops! Something went wrong.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              We're sorry, but something unexpected happened. Please try
              refreshing the page or go back to the homepage.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleGoHome}
                sx={{ mb: 2, mr: 2 }}
              >
                Go to Home
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={this.handleLogoutAndReset}
                sx={{ mb: 2 }}
              >
                Log out and try again
              </Button>
            </Box>

            <Collapse in={this.state.showDetails}>
              <Box
                sx={{
                  backgroundColor: "#fff3f3",
                  p: "1rem",
                  borderRadius: "0.5rem",
                  mb: 2,
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="error">
                  {this.state.error?.toString()}
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(this.state.error?.toString() || "")
                      .then(() => {
                        this.setState({ copied: true });
                        setTimeout(
                          () => this.setState({ copied: false }),
                          2000
                        );
                      });
                  }}
                  sx={{ mt: 1 }}
                >
                  Copy Error
                </Button>
                {this.state.copied && (
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ mt: 1 }}
                  >
                    Copied!
                  </Typography>
                )}
              </Box>
            </Collapse>

            <Button
              variant="text"
              onClick={() =>
                this.setState((prev) => ({ showDetails: !prev.showDetails }))
              }
            >
              {this.state.showDetails ? "Hide Details" : "Show Details"}
            </Button>

            <Typography variant="body2" sx={{ mt: 2 }}>
              If the issue persists, please contact{" "}
              <a
                href={`mailto:hello@deovise.com?subject=App%20Error&body=Error:%20${encodeURIComponent(
                  this.state.error?.toString() || ""
                )}`}
                style={{ textDecoration: "underline" }}
              >
                hello@deovise.com
              </a>{" "}
              and include the copied error message.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
}

// Redux connection
const mapDispatchToProps = {
  setLogout,
};

const ConnectedErrorBoundaryInner = connect(
  null,
  mapDispatchToProps
)(ErrorBoundaryInner);

// Functional wrapper that injects media query and theme
function ErrorBoundary(props) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery("(min-width: 1000px)");

  return (
    <ConnectedErrorBoundaryInner
      {...props}
      theme={theme}
      isLargeScreen={isLargeScreen}
    />
  );
}

export default ErrorBoundary;
